import { FC, useMemo } from "react";
import { useAppSelector } from "@/store/hooks";
import { selectedResourcesByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesByMasterIdSelector";
import { TooltipWithContent } from "@/components/common/TooltipWithContent";
import { TaskCreateActionItemTooltipContent } from "@/components/pages/common/task-management/components/common/TaskCreateActionItemTooltipContent";
import { LiveUsageResourceGridCreateTaskAction } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/task-create/LiveUsageResourceGridCreateTaskAction";
import { getUserPermissionInfoText } from "@/components/pages/utils/helpers/getUserPermissionInfoText";
import { liveUsageRowActionTooltipTitles } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/utils/constants/constants";
import { isResourceKubernetes } from "@/store/resources-grid/utils/helpers/isResourceKubernetes";
import { useAppAbility } from "@/services/permissions";
import { getTaskNameByResourceType } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/utils/helpers/getTaskNameByResourceType";
import { ResourceDataType } from "@/store/live-usage-mgmt/utils/types/types";

interface LiveUsageResourceGridCreateTaskToolbarActionProps {
  disabled?: boolean;
  masterGridId: string;
}

export const LiveUsageResourceGridCreateTaskToolbarAction: FC<
  LiveUsageResourceGridCreateTaskToolbarActionProps
> = ({ masterGridId, disabled }) => {
  const { cannot } = useAppAbility();
  const cannotCreateTask = cannot("create", "task");

  const selectedResources = useAppSelector((state) =>
    selectedResourcesByMasterIdSelector(state, masterGridId),
  );

  const targetsWithTasks = useMemo(
    () =>
      selectedResources
        ?.filter(
          (resource) =>
            resource && !isResourceKubernetes(resource) && !!resource.task,
        )
        ?.map((resource) => {
          if (!resource || isResourceKubernetes(resource)) {
            return null;
          }

          return {
            task: resource.task,
            targetName: getTaskNameByResourceType({
              resource,
            } as ResourceDataType),
            targetId: resource.provider_unique_identifier ?? resource.id,
          };
        }),
    [selectedResources],
  );

  const atLeastOneTaskExist = !!targetsWithTasks?.length;
  const allHadTask = targetsWithTasks?.length === selectedResources?.length;
  const buttonDisabled = disabled || cannotCreateTask || allHadTask;

  const tooltip = getToolbarTaskCreateActionItemTooltipText({
    disabled,
    cannotCreateTask,
  });

  return (
    <TooltipWithContent
      title={
        atLeastOneTaskExist ? (
          <TaskCreateActionItemTooltipContent
            targets={targetsWithTasks}
            allTargetsHasTasks={
              targetsWithTasks?.length === selectedResources?.length
            }
          />
        ) : (
          (tooltip ?? "")
        )
      }
    >
      <span>
        <LiveUsageResourceGridCreateTaskAction
          masterGridId={masterGridId}
          disabled={buttonDisabled}
        />
      </span>
    </TooltipWithContent>
  );
};

const getToolbarTaskCreateActionItemTooltipText = ({
  disabled,
  cannotCreateTask,
}: {
  disabled?: boolean;
  cannotCreateTask?: boolean;
}) => {
  if (cannotCreateTask) {
    return getUserPermissionInfoText("Create Task");
  }

  return liveUsageRowActionTooltipTitles.crate_task[
    disabled ? "disabled" : "active"
  ];
};
